import { z } from "@/zod";
import {
  city,
  streetNameOptional,
  streetNumber,
  apartmentNumber,
  postalCode,
  birthDate,
  pesel,
  phoneNumber,
  dateTime,
  bankAccountNumber,
  taxIdentificationNumber,
} from "./commonFields";
import { AGREEMENT_STATUS } from "@/types/agreement";
import { isInstructionalVideosRequired } from "@/modules/agreement/questionnaires/utils";
import type {
  ConditionalFieldConfig,
  WriteQuestionnaire,
} from "@/types/questionnaire";
import {
  DISABILITY_PENSION_TYPE,
  DISABILITY_PENSION_TYPE_VALUES,
} from "@/types/questionnaire";
import { applyEmptyStringsToNulls } from "@/lib/utils/zod";
import { isEmpty } from "@/lib/utils/isEmpty";
import { isEmptyArray } from "@/lib/utils/isEmptyArray";
import { GENDER } from "@/types/common";

// Questionnaire comment schema
export const questionnaireComment = z.object({
  id: z.string().optional(),
  comment: z.string(),
  created_by_name: z.string().optional(),
  created_by_role_name: z.string().optional(),
  created_at: dateTime.optional(),
});

export const questionnaireComments = z.array(questionnaireComment).optional();

export const regulationsSchema = z.record(
  z.string(), // quarter key ("q1", "q2", "q3", "q4")
  z.array(z.string()),
);

// Attachment schema
const attachmentSchema = z.object({
  s3_key: z.string().min(1),
  filename: z.string().min(1),
});

// Base questionnaire schema
export const questionnaireDataBase = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  full_name: z.string().nullable().optional(),
  city,
  postal_code: postalCode,
  post_office: z.string().min(1),
  street: streetNameOptional.nullish(),
  house_number: streetNumber,
  apartment_number: apartmentNumber.nullish(),
  province_id: z.string().min(1),
  province_name: z.string().nullable().optional(),
  province_nfz_code: z.string().nullable().optional(),
  province_nfz_description: z.string().nullable().optional(),
  county: z.string().min(1),
  municipality: z.string().min(1),
  email: z.string().email(),
  phone: phoneNumber,
  birth_date: birthDate,
  personal_id_number: pesel,
  gender: z.enum(GENDER),
  birth_place: z.string().min(1),
  bank_account_number: bankAccountNumber,
  tax_office_id: z.string().min(1),
  tax_office_name: z.string().nullable().optional(),
  citizenship_id: z.string().min(1),
  citizenship_name: z.string().nullable().optional(),
  disability_certificate_id: z.string().min(1),
  disability_certificate_code: z.string().nullable().optional(),
  rodo_accepted: z.boolean().refine((val) => val, {
    message: "questionnaires.validation.rodoNeedsToBeAccepted",
  }),
  regulations_accepted: z.boolean().refine((val) => val, {
    message: "questionnaires.validation.regulationsNeedsToBeAccepted",
  }),
  // B2B fields
  company_tax_identification_number: taxIdentificationNumber.nullable(),
  company_name: z.string().nullable(),
  company_postal_code: postalCode.nullable(),
  company_city: z.string().nullable(),
  company_street_name: z.string().nullable(),
  company_street_number: z.string().nullable(),
  company_apartment_number: apartmentNumber.nullish(),
  is_b2b_agreement: z.boolean().optional(),
});

// Base statements schema
export const questionnaireStatementsBase = z.object({
  is_student: z.boolean(),
  school_name: z.string().nullable(),
  student_id_number: z.string().nullable(),
  student_id_valid_until: z.string().date().nullable(),
  has_valid_visa: z.boolean(),
  visa_number: z.string().nullable(),
  visa_valid_until: z.string().date().nullable(),
  has_polish_card: z.boolean(),
  polish_card_valid_until: z.string().date().nullable(),
  has_residence_card: z.boolean(),
  residence_card_valid_until: z.string().date().nullable(),
  is_employed: z.boolean(),
  employer_name: z.string().nullable(),
  employment_certificate_issue_date: z.string().date().nullable(),
  voluntary_sickness_insurance: z.boolean(),
  is_pensioner: z.boolean(),
  pension_number: z.string().nullable(),
  pension_start_date: z.string().date().nullable(),
  pension_office_name: z.string().nullable(),
  watched_instructional_videos: z.boolean(),
  is_disability_pensioner: z.boolean(),
  disability_pension_number: z.string().nullable(),
  disability_pension_start_date: z.string().date().nullable(),
  disability_pension_end_date: z.string().date().nullable(),
  disability_pension_office_name: z.string().nullable(),
  disability_pension_type: z.enum(DISABILITY_PENSION_TYPE_VALUES).nullable(),
  disability_pension_description: z.string().nullable(),
});

// File metadata schema
export const fileMetadata = z.object({
  path: z.string(),
  size: z.string(),
  confirmed_at: dateTime.nullable(),
});

// Questionnaire attachments schema
export const questionnaireAttachments = z.object({
  student_id_attachments: z.array(fileMetadata),
  visa_attachments: z.array(fileMetadata),
  polish_card_attachments: z.array(fileMetadata),
  residence_card_attachments: z.array(fileMetadata),
  employment_certificate_attachments: z.array(fileMetadata),
  attachments: z.array(fileMetadata),
});

export const CONDITIONAL_FIELDS_CONFIG: ConditionalFieldConfig[] = [
  {
    checkbox: "is_student",
    dependentFields: [
      "school_name",
      "student_id_number",
      "student_id_valid_until",
      "student_id_attachments",
    ],
    requiredFields: [
      "school_name",
      "student_id_number",
      "student_id_valid_until",
      "student_id_attachments",
    ],
    attachmentFields: ["student_id_attachments"],
  },
  {
    checkbox: "has_valid_visa",
    dependentFields: ["visa_number", "visa_valid_until", "visa_attachments"],
    requiredFields: ["visa_number", "visa_valid_until", "visa_attachments"],
    attachmentFields: ["visa_attachments"],
  },
  {
    checkbox: "has_polish_card",
    dependentFields: ["polish_card_valid_until", "polish_card_attachments"],
    requiredFields: ["polish_card_valid_until", "polish_card_attachments"],
    attachmentFields: ["polish_card_attachments"],
  },
  {
    checkbox: "has_residence_card",
    dependentFields: [
      "residence_card_valid_until",
      "residence_card_attachments",
    ],
    requiredFields: [
      "residence_card_valid_until",
      "residence_card_attachments",
    ],
    attachmentFields: ["residence_card_attachments"],
  },
  {
    checkbox: "is_employed",
    dependentFields: [
      "employer_name",
      "employment_certificate_issue_date",
      "employment_certificate_attachments",
    ],
    requiredFields: ["employer_name"],
    attachmentFields: ["employment_certificate_attachments"],
  },
  {
    checkbox: "is_pensioner",
    dependentFields: [
      "pension_number",
      "pension_start_date",
      "pension_office_name",
    ],
    requiredFields: [
      "pension_number",
      "pension_start_date",
      "pension_office_name",
    ],
  },
  {
    checkbox: "is_disability_pensioner",
    dependentFields: [
      "disability_pension_number",
      "disability_pension_start_date",
      "disability_pension_end_date",
      "disability_pension_office_name",
      "disability_pension_type",
      "disability_pension_description",
    ],
    requiredFields: [
      "disability_pension_number",
      "disability_pension_start_date",
      "disability_pension_end_date",
      "disability_pension_office_name",
      "disability_pension_type",
    ],
  },
];

// B2B fields configuration for validation
const B2B_REQUIRED_FIELDS: {
  field: string;
  message: string;
}[] = [
  {
    field: "company_tax_identification_number",
    message: "questionnaires.validation.companyTaxIdentificationNumberRequired",
  },
  {
    field: "company_name",
    message: "questionnaires.validation.companyNameRequired",
  },
  {
    field: "company_postal_code",
    message: "questionnaires.validation.companyPostalCodeRequired",
  },
  {
    field: "company_city",
    message: "questionnaires.validation.companyCityRequired",
  },
  {
    field: "company_street_name",
    message: "questionnaires.validation.companyStreetNameRequired",
  },
  {
    field: "company_street_number",
    message: "questionnaires.validation.companyStreetNumberRequired",
  },
] as const;

const B2B_COMPANY_FIELDS: (keyof WriteQuestionnaire)[] = [
  "company_tax_identification_number",
  "company_name",
  "company_postal_code",
  "company_city",
  "company_street_name",
  "company_street_number",
  "company_apartment_number",
];

export const writeQuestionnaireAttachmentsShape = {
  student_id_attachments: z.array(attachmentSchema).nullable().optional(),
  visa_attachments: z.array(attachmentSchema).nullable().optional(),
  polish_card_attachments: z.array(attachmentSchema).nullable().optional(),
  residence_card_attachments: z.array(attachmentSchema).nullable().optional(),
  employment_certificate_attachments: z
    .array(attachmentSchema)
    .nullable()
    .optional(),
  attachments: z.array(attachmentSchema).nullable().optional(),
};

const questionnaireShape = {
  ...questionnaireDataBase.shape,
  ...questionnaireStatementsBase.shape,
  ...writeQuestionnaireAttachmentsShape,
} as const;

export const writeQuestionnaire = applyEmptyStringsToNulls(questionnaireShape)
  .superRefine((data: Record<string, unknown>, ctx: z.RefinementCtx) => {
    // Validate required fields when conditional checkboxes are checked
    CONDITIONAL_FIELDS_CONFIG.forEach(
      ({ checkbox, requiredFields, attachmentFields = [] }) => {
        if (!data[checkbox]) return;

        requiredFields.forEach((field) => {
          const value = data[field];
          const isAttachment = attachmentFields.includes(field);

          const isInvalid = isAttachment ? isEmptyArray(value) : isEmpty(value);

          if (!isInvalid) return;

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [String(field)],
            ...(isAttachment && {
              message: "questionnaires.validation.attachmentRequired",
            }),
          });
        });
      },
    );

    // Validate B2B fields when is_b2b_agreement is true
    const isB2B = data.is_b2b_agreement as boolean | undefined;
    if (isB2B) {
      B2B_REQUIRED_FIELDS.forEach(({ field, message }) => {
        const value = data[field];
        if (isEmpty(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message,
          });
        }
      });
    }

    // Validate instructional videos requirement based on age and status
    if (
      isInstructionalVideosRequired(
        data.birth_date as string,
        data.is_student as boolean,
        data.is_employed as boolean,
      ) &&
      !(data.watched_instructional_videos as boolean)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["watched_instructional_videos"],
        message: "questionnaires.validation.instructionalVideosRequired",
      });
    }

    // Validate disability pension description when type is "other"
    if (
      data.is_disability_pensioner &&
      data.disability_pension_type === DISABILITY_PENSION_TYPE.OTHER &&
      isEmpty(data.disability_pension_description)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["disability_pension_description"],
        message:
          "questionnaires.validation.disabilityPensionDescriptionRequired",
      });
    }
  })
  .transform((data): typeof data => {
    const dataRecord = data as Record<keyof WriteQuestionnaire, unknown>;

    // Clear dependent fields when conditional checkboxes are unchecked
    CONDITIONAL_FIELDS_CONFIG.forEach(({ checkbox, dependentFields }) => {
      if (!dataRecord[checkbox]) {
        dependentFields.forEach((field) => {
          dataRecord[field] = null;
        });
      }
    });

    // Convert empty attachment arrays to null (required by backend)
    const attachmentFields: (keyof WriteQuestionnaire)[] = [
      ...CONDITIONAL_FIELDS_CONFIG.flatMap(
        (config) => config.attachmentFields || [],
      ),
      "attachments",
    ];

    attachmentFields.forEach((field) => {
      if (isEmptyArray(dataRecord[field])) {
        dataRecord[field] = null;
      }
    });

    // Disable voluntary sickness insurance for students and employed
    if (data.is_student || data.is_employed) {
      dataRecord.voluntary_sickness_insurance = false;
    }

    // For non-B2B payloads, always clear company fields.
    if (!data.is_b2b_agreement) {
      B2B_COMPANY_FIELDS.forEach((field) => {
        dataRecord[field] = null;
      });
    }

    // Clear disability pension description when type is not "other"
    if (
      data.is_disability_pensioner &&
      data.disability_pension_type !== DISABILITY_PENSION_TYPE.OTHER
    ) {
      dataRecord.disability_pension_description = null;
    }

    // Remove fields that should not be sent to backend
    const {
      full_name,
      province_name,
      province_nfz_code,
      province_nfz_description,
      tax_office_name,
      citizenship_name,
      disability_certificate_code,
      ...rest
    } = data;
    return rest;
  });

export const amendStatementsQuestionnaireSchema = z
  .object({
    rodo_accepted: z.boolean().refine((val) => val, {
      message: "questionnaires.validation.rodoNeedsToBeAccepted",
    }),
    regulations_accepted: z.boolean().refine((val) => val, {
      message: "questionnaires.validation.regulationsNeedsToBeAccepted",
    }),
  })
  .passthrough();

export const readQuestionnaire = z.object({
  questionnaire: writeQuestionnaire
    .and(
      z.object({
        agreement_id: z.string(),
        agreement_template_code: z.string(),
        agreement_status: z.enum(AGREEMENT_STATUS),
        comments: questionnaireComments,
        is_b2b_agreement: z.boolean().optional(),
      }),
    )
    .readonly(),
  regulations: regulationsSchema,
  attachments: questionnaireAttachments,
  can_be_rejected_by_contractor: z.boolean(),
});
